import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { DollarSign, AlertCircle, Loader } from 'lucide-react';
import { useValidateCampaignForm } from '../../hooks/useValidateCampaignForm';
import type { CampaignFormData, RetainerDocument } from '../../types/campaign';
import DatePicker from '../../elements/DatePicker';
import FormField from '../../elements/FormField';
import { Select } from '../../elements/Select';
import { useToast } from '../../hooks/useToast';
import { useAppDispatch } from '../../hooks/useAppDipatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { setCreationStep, setIsCampaignCreated } from '../../features/campaigns/campaignsSlice';
import { useNavigate } from 'react-router-dom';
import { PhoneNumberInput } from '../../elements/PhoneNumberInput';
import { TimePicker } from '../../elements/TimePicker';
import { TIMEZONES } from '../../data/constants';
import { CampaignController } from '../../api/controllers/campaign.controller';
import { Accordion } from '../../elements/Accordion';
import { formatObjective } from '../../utils/common';
import RetainerDocumentUpload from './RetainerDocumentUpload';
import BudgetTierCards from './BudgetTierCards';
import { useBudgetPreview } from '../../contexts/BudgetPreviewContext';
import { BUDGET_TIERS, getFloorPriceForClaimType } from '../../utils/budgetTiers';

interface CreateCampaignFormProps {
  defaultValues: CampaignFormData | null;
  onSubmit: (campaign: CampaignFormData, retainerFile?: File | null) => Promise<void>;
  isEdit?: boolean;
  existingRetainerDoc?: RetainerDocument | null;
}

export default function CreateCampaignForm({
  defaultValues,
  onSubmit,
  isEdit = false,
  existingRetainerDoc,
}: CreateCampaignFormProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const dispatch = useAppDispatch();
  const [objectives, setObjectives] = useState<{ label: string; value: string }[]>([]);
  const [claimTypes, setClaimTypes] = useState<
    { label: string; value: string; claim_id: string }[]
  >([]);
  const [questions, setQuestions] = useState<{ text: string }[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState<'loading' | null>(null);
  const userTimezoneValue = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const userTimezoneOption = TIMEZONES.find((tz) => tz.value === userTimezoneValue);

  const initialTimezoneOption = userTimezoneOption || {
    label: 'Coordinated Universal Time (UTC)',
    value: 'UTC',
  };

  const { isCampaignCreated, campaignObjective } = useAppSelector((state) => state.campaigns);

  const [formData, setFormData] = useState<CampaignFormData>({
    name: defaultValues?.name ?? '',
    objective: defaultValues?.objective ?? (objectives.length > 0 ? objectives[0].value : ''),
    claim_type: defaultValues?.claim_type ?? (claimTypes.length > 0 ? claimTypes[0].value : ''),
    destination_phone_number: defaultValues?.destination_phone_number ?? '',
    daily_budget: defaultValues?.daily_budget ?? '50',
    total_budget: defaultValues?.total_budget ?? '',
    start_at: defaultValues?.start_at ?? new Date(),
    end_at: defaultValues?.end_at ?? null,
    start_time: defaultValues?.start_time ?? '08:00',
    end_time: defaultValues?.end_time ?? '20:00',
    timezone: defaultValues?.timezone ?? initialTimezoneOption.value,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [retainerFile, setRetainerFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTier, setSelectedTier] = useState<number | null>(null);

  const { setPreview } = useBudgetPreview();

  const currentFloorPrice = useMemo(() => {
    return getFloorPriceForClaimType(campaignObjective, formData.claim_type);
  }, [campaignObjective, formData.claim_type]);

  // Sync budget preview context for the sidebar
  useEffect(() => {
    setPreview({
      dailyBudget: parseFloat(formData.daily_budget) || 0,
      floorPrice: currentFloorPrice,
      claimType: formData.claim_type,
    });
  }, [formData.daily_budget, formData.claim_type, currentFloorPrice, setPreview]);

  // Reset budget to Tier 1 when claim type changes (create mode only)
  useEffect(() => {
    if (!formData.claim_type || isEdit) return;
    const newFloor = getFloorPriceForClaimType(campaignObjective, formData.claim_type);
    const tier1Budget = BUDGET_TIERS[0].calculateBudget(newFloor);
    setFormData((prev) => ({ ...prev, daily_budget: String(tier1Budget) }));
    setSelectedTier(1);
    setErrors((prev) => ({ ...prev, dailyBudget: '' }));
  }, [formData.claim_type, campaignObjective, isEdit]);

  const handleTierSelect = (tierId: number, budgetValue: number) => {
    setSelectedTier(tierId);
    setFormData((prev) => ({ ...prev, daily_budget: String(budgetValue) }));
    setErrors((prev) => ({ ...prev, dailyBudget: '' }));
  };

  const handleCustomBudgetChange = (value: string) => {
    setFormData((prev) => ({ ...prev, daily_budget: value }));
    const matchingTier = BUDGET_TIERS.find(
      (tier) => tier.calculateBudget(currentFloorPrice) === parseFloat(value),
    );
    setSelectedTier(matchingTier ? matchingTier.id : null);
    setErrors((prev) => ({ ...prev, dailyBudget: '' }));
  };

  const runSubmit = async (): Promise<boolean> => {
    const validationErrors = useValidateCampaignForm(formData, currentFloorPrice);
    const combinedErrors = validationErrors.reduce<Record<string, string>>(
      (acc, error) => ({
        ...acc,
        [error.field]: error.message,
      }),
      {}
    );

    if (Object.keys(combinedErrors).length > 0) {
      const firstErrorMessage = Object.values(combinedErrors)[0];
      if (firstErrorMessage) {
        showToast(firstErrorMessage, 'error');
      }
      if (combinedErrors) {
        setErrors(combinedErrors);
      } else {
        setErrors({});
      }
      return false;
    }

    const daily = parseFloat(formData.daily_budget || '0');
    const total = parseFloat(formData.total_budget || '0');

    if (formData.total_budget && total !== 0 && total < daily * 7) {
      setErrors((prev) => ({
        ...prev,
        totalBudget: 'Minimum Total budget must equal or exceed 7 days of daily budget.',
      }));
      showToast('Minimum Total budget must equal or exceed 7 days of daily budget.', 'error');
      return false;
    }

    if (formData.objective.toLowerCase() === 'signed_retainers' && !retainerFile && !isEdit) {
      showToast('Retainer document is required for signed retainers campaigns.', 'error');
      setErrors((prev) => ({ ...prev, retainerFile: 'Retainer document is required.' }));
      return false;
    }

    setErrors({});
    setIsSubmitting(true);
    try {
      await onSubmit(formData, retainerFile);
      return true;
    } catch (error) {
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isSuccess = await runSubmit();
    if (isSuccess) {
      dispatch(setCreationStep('markets'));
    }
  };

  const handleCreateAndNext = async () => {
    const isSuccess = await runSubmit();
    if (isSuccess) {
      dispatch(setCreationStep('none'));
    }
  };

  useEffect(() => {
    if (isCampaignCreated && !isEdit) {
      dispatch(setIsCampaignCreated(false));
    }
  }, [isCampaignCreated, isEdit, dispatch]);

  useEffect(() => {
    const uniqueObjectivesFromState = Array.from(
      new Set(campaignObjective.map((item: any) => item.campaign_objective)),
    ).filter(Boolean) as string[];

    const allObjectives = Array.from(new Set(uniqueObjectivesFromState));

    const formattedObjectives = allObjectives.map((action) => ({
      value: action,
      label: formatObjective(action),
    }));
    setObjectives(formattedObjectives);

    setFormData((prev) => {
      if (prev.objective === '' && formattedObjectives.length > 0) {
        const defaultObjective = formattedObjectives[0].value;
        return { ...prev, objective: defaultObjective };
      }
      return prev;
    });
  }, [campaignObjective]);

  useEffect(() => {
    if (formData.objective === '' || campaignObjective.length === 0) {
      setClaimTypes([]);
      return;
    }

    if (formData.objective.toLowerCase() !== 'qualified_calls') {
      setFormData((prev) => ({
        ...prev,
        destination_phone_number: '',
        start_time: '08:00',
        end_time: '20:00',
        timezone: initialTimezoneOption.value,
      }));
      setErrors((prev) => ({
        ...prev,
        destination_phone_number: '',
        start_time: '',
        end_time: '',
        timezone: '',
      }));
    }

    const uniqueClaimTypes: { type: string; id: string }[] = Array.from(
      new Set(
        campaignObjective
          .filter((item: any) => item.campaign_objective === formData.objective)
          .map((item: any) => {
            return {
              type: item.claim_type,
              id: item.claim_id,
            };
          }),
      ),
    );

    const formattedClaimTypes = uniqueClaimTypes.map(
      (claim_type: { type: string; id: string }) => ({
        value: claim_type.type,
        label: claim_type.type,
        claim_id: claim_type.id,
      }),
    );
    setClaimTypes(formattedClaimTypes);

    setFormData((prev) => {
      if (
        formattedClaimTypes.length > 0 &&
        (prev.claim_type === '' || !formattedClaimTypes.some((ct) => ct.value === prev.claim_type))
      ) {
        return { ...prev, claim_type: formattedClaimTypes[0].value };
      }
      return prev;
    });
  }, [formData.objective, campaignObjective]);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (formData.claim_type) {
        const claimType = claimTypes.find((c) => c.value === formData.claim_type);

        if (!claimType) {
          setQuestions([]);
          return;
        }

        setLoadingQuestions('loading');

        try {
          const result: any = await CampaignController.getQuestionsByClaimType({
            claim_id: claimType.claim_id,
          });

          if (result) {
            setQuestions(result.data);
            return;
          }
        } finally {
          setLoadingQuestions(null);
        }
      } else {
        setQuestions([]);
      }
    };

    fetchQuestions();
  }, [formData.claim_type, claimTypes]);

  useEffect(() => {
    if (!defaultValues) return;

    setFormData({
      ...defaultValues,
      timezone: defaultValues.timezone || initialTimezoneOption.value,
    });
  }, [defaultValues, initialTimezoneOption.value]);

  return (
    <div className="glass-card rounded-xl">
      <div className="border-b border-gray-700/50 p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-neutral-light">
          {isEdit ? 'Edit Campaign' : 'Create New Campaign'}
        </h2>
        <p className="mt-2 text-sm sm:text-base text-text-muted">
          {isEdit
            ? 'Update the information below to edit your campaign.'
            : 'Fill out the information below to create your campaign.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-4 sm:p-6">
        <div className="space-y-6 sm:space-y-8">
          {/* Campaign Name */}
          <FormField label="Campaign Name" required highlight error={errors.name}>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-white/5 border border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50 focus:ring-2 focus:ring-accent/20"
              placeholder="e.g. Summer Lead Generation 2024"
              required
            />
          </FormField>

          {/* Objective + Claim Type Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <FormField label="Campaign Objective" required highlight error={errors.objective}>
              <Select
                type="button"
                name="objective"
                options={objectives}
                defaultOption={objectives.find((item) => item.value === formData.objective)}
                onSelect={(option) => {
                  setErrors((prev) => ({ ...prev, objective: '' }));
                  setFormData({ ...formData, objective: option.value });
                }}
              />
            </FormField>

            <FormField label="Claim Type" required highlight error={errors.claim_type}>
              <Select
                type="button"
                name="claim_type"
                options={claimTypes}
                defaultOption={claimTypes.find((item) => item.value === formData.claim_type)}
                onSelect={(option) => {
                  setErrors((prev) => ({ ...prev, claim_type: '' }));
                  setFormData({ ...formData, claim_type: option.value });
                }}
              />
            </FormField>
          </div>

          {/* Budget Section */}
          {!isEdit ? (
            <BudgetTierCards
              floorPrice={currentFloorPrice}
              selectedTier={selectedTier}
              customBudget={formData.daily_budget}
              onTierSelect={handleTierSelect}
              onCustomBudgetChange={handleCustomBudgetChange}
              error={errors.daily_budget}
            />
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-neutral-light">Daily Budget</label>
                <span className="text-xs text-accent">Min. ${currentFloorPrice}</span>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-text-muted" />
                </div>
                <input
                  type="number"
                  value={formData.daily_budget}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      daily_budget: e.target.value,
                    })
                  }
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50 focus:ring-2 focus:ring-accent/20"
                  placeholder="Enter daily budget"
                  required
                  min={currentFloorPrice}
                  step="1"
                />
              </div>
              {errors.daily_budget && (
                <div className="flex items-center gap-2 mt-1.5">
                  <AlertCircle className="w-4 h-4 text-rose-400" />
                  <span className="text-xs text-rose-400">{errors.daily_budget}</span>
                </div>
              )}
            </div>
          )}

          {/* Total Budget + Schedule Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Total Budget */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-neutral-light">Total Budget</label>
                <span className="text-xs text-text-muted">Optional</span>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-text-muted" />
                </div>
                <input
                  type="number"
                  value={formData.total_budget}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      total_budget: e.target.value,
                    })
                  }
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50"
                  placeholder="Enter total budget (optional)"
                />
              </div>
              {errors.totalBudget && (
                <div className="flex items-center gap-2 mt-1.5">
                  <AlertCircle className="w-4 h-4 text-rose-400" />
                  <span className="text-xs text-rose-400">{errors.totalBudget}</span>
                </div>
              )}
            </div>

            {/* Campaign Schedule */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <DatePicker
                  label="Start Date"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-gray-700/50 rounded-lg text-neutral-light focus:outline-none focus:border-accent/50"
                  value={
                    formData.start_at && !isNaN(new Date(formData.start_at).getTime())
                      ? format(new Date(formData.start_at), 'yyyy-MM-dd')
                      : ''
                  }
                  // minDate={format(new Date(), 'yyyy-MM-dd')}
                  required
                  error={errors.startDate}
                  onChange={(date: Date | null) => setFormData({ ...formData, start_at: date! })}
                />
              </div>

              <div className="space-y-2">
                <DatePicker
                  label="End Date"
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-gray-700/50 rounded-lg text-neutral-light focus:outline-none focus:border-accent/50"
                  value={
                    formData.end_at && !isNaN(new Date(formData.end_at).getTime())
                      ? format(new Date(formData.end_at), 'yyyy-MM-dd')
                      : ''
                  }
                  minDate={
                    formData.start_at && !isNaN(new Date(formData.start_at).getTime())
                      ? format(new Date(formData.start_at), 'yyyy-MM-dd')
                      : undefined
                  }
                  error={errors.endDate}
                  onChange={(date: Date | null) => setFormData({ ...formData, end_at: date })}
                />
              </div>
            </div>
          </div>

          {formData.claim_type && (
            <Accordion
              title="Consumer Questions Asked"
              icon={
                loadingQuestions === 'loading' ? (
                  <Loader size={18} className="animate-spin" />
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700">
                    {formData.claim_type}
                  </span>
                )
              }
            >
              {loadingQuestions === 'loading' ? (
                <div className="flex justify-center p-4">
                  <Loader size={18} className="animate-spin" />
                </div>
              ) : questions.length > 0 ? (
                <ul className="space-y-3">
                  {questions.map((q, index) => (
                    <li key={index} className="text-neutral-light text-sm flex items-start">
                      <span className="mr-2 text-accent">&#8226;</span>
                      <span>{q.text}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-text-muted">No questions to display.</p>
              )}
            </Accordion>
          )}

          {formData.objective.toLowerCase() === 'qualified_calls' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <FormField label="Destination Phone Number" required highlight>
                <PhoneNumberInput
                  value={formData.destination_phone_number}
                  onChange={(formattedValue) =>
                    setFormData({ ...formData, destination_phone_number: formattedValue })
                  }
                  placeholder="+1 310 555 1234"
                  className="w-full px-4 py-3 bg-white/5 border border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50 focus:ring-2 focus:ring-accent/20"
                />
                <p className="mt-1 text-xs text-text-muted">Enter Your Intake Phone Number</p>
              </FormField>
              <FormField label="Campaign Timezone" required highlight error={errors.timezone}>
                <Select
                  type="button"
                  name="timezone"
                  options={TIMEZONES}
                  defaultOption={
                    formData.timezone
                      ? TIMEZONES.find((tz) => tz.value === formData.timezone)
                      : TIMEZONES[0]
                  }
                  onSelect={(option) => {
                    setFormData({ ...formData, timezone: option.value });
                  }}
                />
              </FormField>
              <FormField label="Call Start Time" required highlight>
                <TimePicker
                  value={formData.start_time!}
                  onChange={(value) => setFormData({ ...formData, start_time: value })}
                  required
                />
              </FormField>
              <FormField label="Call End Time" required highlight>
                <TimePicker
                  value={formData.end_time!}
                  onChange={(value) => setFormData({ ...formData, end_time: value })}
                  required
                />
              </FormField>
            </div>
          )}

          {formData.objective.toLowerCase() === 'signed_retainers' && (
            <div>
              <RetainerDocumentUpload
                file={retainerFile}
                onFileSelect={setRetainerFile}
                existingDocument={
                  isEdit && existingRetainerDoc
                    ? { filename: existingRetainerDoc.original_name, status: existingRetainerDoc.status }
                    : null
                }
                uploadStatus={isSubmitting && retainerFile ? 'uploading' : 'idle'}
                error={errors.retainerFile}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6 sm:mt-8 pt-6 border-t border-gray-700/50">
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => {
              dispatch(setCreationStep('none'));
              navigate('/campaigns');
            }}
            className="order-last sm:order-first px-6 py-2 text-sm text-text-muted hover:text-neutral-light transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-5">
            {!isEdit && (
              <button
                type="button"
                disabled={isSubmitting}
                className="px-6 py-2.5 sm:py-2 bg-gray-600 hover:bg-brand-hover text-neutral-light font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={async () => handleCreateAndNext()}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Loader size={16} className="animate-spin" />
                    Saving...
                  </span>
                ) : (
                  'Save Draft'
                )}
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 sm:py-2 bg-accent hover:bg-accent-hover text-brand-bg font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader size={16} className="animate-spin" />
                  Saving...
                </span>
              ) : isEdit ? (
                'Save Changes'
              ) : (
                'Save & Continue'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
